import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, TextField, Typography, CircularProgress, Tooltip } from '@mui/material';
import { ReloadOutlined, SendOutlined, PaperClipOutlined } from '@ant-design/icons';
import api from 'api/axios';

// ---------- helpers ----------
function formatWaNumber(chatId) {
  if (!chatId) return '-';
  const parts = String(chatId).split('@');
  const raw = parts[0];
  if (!raw) return chatId;

  if (raw.startsWith('62')) {
    const rest = raw.slice(2);
    if (/^8\d{7,11}$/.test(rest)) {
      const a = rest.slice(0, 3);
      const b = rest.slice(3, 7);
      const c = rest.slice(7);
      if (c) return `+62 ${a}-${b}-${c}`;
      return `+62 ${a}-${b}`;
    }
    return `+${raw}`;
  }

  if (raw.startsWith('1') && raw.length >= 10 && raw.length <= 11) {
    const area = raw.slice(1, 4);
    const mid = raw.slice(4, 7);
    const last = raw.slice(7);
    return `+1 (${area}) ${mid}-${last}`;
  }

  return `+${raw}`;
}

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function isLikelyNumberQuery(q) {
  const s = String(q || '')
    .trim()
    .replace(/\s+/g, '');
  if (!s) return false;
  return /^[+]?\d{6,16}$/.test(s);
}

function normalizeChatIdInputToWaId(input) {
  const s = String(input || '')
    .trim()
    .replace(/\s+/g, '');
  if (!s) return '';
  let digits = s.replace(/^\+/, '');
  if (digits.startsWith('08')) digits = `62${digits.slice(1)}`;
  if (s.includes('@')) return s;
  return `${digits}@c.us`;
}

function buildMediaDataUrl(mime, base64) {
  if (!mime || !base64) return null;
  return `data:${mime};base64,${base64}`;
}

function extractTextFromTemplateObject(tpl) {
  if (!tpl || !Array.isArray(tpl.components)) return '';
  const bodyComp = tpl.components.find((c) => c?.type === 'body');
  const p0 = bodyComp?.parameters?.[0];
  const text = p0?.text;
  return typeof text === 'string' ? text : '';
}

function normalizeWsMessage(raw) {
  const sessionName = raw?.sessionName || '';
  const direction = raw?.direction === 'outgoing' ? 'outgoing' : 'incoming';
  const m = raw?.message || {};

  const messageId = m.id || raw?.messageId || '';
  const from = m.from || raw?.fromWaId || '';
  const to = m.to || raw?.toNumber || '';

  // NOTE: chatId bisa salah kalau from/to kebalik dari library WA.
  // Kita akan perbaiki di ws.onmessage menggunakan event chat_unread jika ada.
  const chatId = direction === 'incoming' ? from : to;

  const tplText =
    extractTextFromTemplateObject(m?.template) ||
    extractTextFromTemplateObject(raw?.template) ||
    extractTextFromTemplateObject(raw?.message?.template);

  const body = m.body ?? m.text ?? m.caption ?? raw?.message ?? tplText ?? '';

  const tsSec = typeof m.timestamp === 'number' ? m.timestamp : typeof m.t === 'number' ? m.t : null;
  const createdAt = raw?.createdAt || m.createdAt || (tsSec ? new Date(tsSec * 1000).toISOString() : new Date().toISOString());

  const status = raw?.status || (typeof m.ack === 'number' ? String(m.ack) : '') || (m.status ? String(m.status) : '');

  return {
    id: raw?.id || messageId || Date.now(),
    messageId,
    sessionName,
    chatId,
    fromWaId: from,
    toNumber: to,
    message: body,
    direction,
    status,
    createdAt,
    source: raw?.source || 'ws',
    mediaBase64: raw?.mediaBase64 || m?.mediaBase64 || null,
    mediaMimeType: raw?.mediaMimeType || m?.mediaMimeType || null,
    mediaUrl: raw?.mediaUrl || m?.mediaUrl || null
  };
}

// detect JSON template string
function isTemplatePayloadString(s) {
  if (typeof s !== 'string') return false;
  const t = s.trim();
  if (!t.startsWith('{') || !t.endsWith('}')) return false;
  try {
    const j = JSON.parse(t);
    return !!(j && typeof j === 'object' && (j.name || j.template?.name) && (j.components || j.template?.components));
  } catch {
    return false;
  }
}

function isPlaceholder(v) {
  const s = String(v || '')
    .trim()
    .toLowerCase();
  return ['[media]', '[image]', '[video]', '[sticker]'].includes(s);
}

// template builder (dipakai hanya kalau ada text)
function buildReplyTemplate(bodyText) {
  const text = String(bodyText || '').trim();
  return {
    name: 'promo_button',
    components: [
      {
        type: 'body',
        parameters: [{ type: 'text', text }]
      }
    ]
  };
}

// ambil body text + url buttons dari template JSON payload
function extractTemplateInfo(input) {
  const result = { bodyText: '', urlButtons: [] };
  if (!input) return result;

  let obj = input;

  if (typeof input === 'string') {
    const s = input.trim();
    if (s.startsWith('{') || s.startsWith('[')) {
      try {
        obj = JSON.parse(s);
      } catch {
        return result;
      }
    } else {
      return result;
    }
  }

  const tpl = obj?.template || obj?.message?.template || obj;
  if (!tpl || !Array.isArray(tpl.components)) return result;

  const bodyComp = tpl.components.find((c) => String(c?.type).toLowerCase() === 'body');
  const bodyParams = Array.isArray(bodyComp?.parameters) ? bodyComp.parameters : [];
  const bodyTexts = bodyParams.filter((p) => String(p?.type).toLowerCase() === 'text' && typeof p?.text === 'string').map((p) => p.text);

  result.bodyText = bodyTexts.join('\n').trim();

  const urlButtons = tpl.components
    .filter((c) => String(c?.type).toLowerCase() === 'button' && String(c?.sub_type).toLowerCase() === 'url')
    .map((btn) => {
      const params = Array.isArray(btn?.parameters) ? btn.parameters : [];
      const p0 = params.find((p) => String(p?.type).toLowerCase() === 'text' && typeof p?.text === 'string');
      return p0?.text?.trim() || '';
    })
    .filter(Boolean);

  result.urlButtons = urlButtons;

  return result;
}

// detect raw base64 media coming inside `message`
function detectBase64MediaInMessage(raw) {
  if (typeof raw !== 'string') return null;
  const s = raw.trim();

  // data URL
  if (s.startsWith('data:') && s.includes(';base64,')) {
    const i = s.indexOf(';base64,');
    const mime = s.slice(5, i);
    const base64 = s.slice(i + ';base64,'.length);
    if (mime && base64) return { mime, base64 };
    return null;
  }

  // pure base64 (heuristic)
  if (s.length < 200) return null;
  const looksB64 = /^[A-Za-z0-9+/=\r\n]+$/.test(s);
  if (!looksB64) return null;

  if (s.startsWith('/9j/')) return { mime: 'image/jpeg', base64: s.replace(/\s+/g, '') };
  if (s.startsWith('iVBOR')) return { mime: 'image/png', base64: s.replace(/\s+/g, '') };
  if (s.startsWith('R0lGOD')) return { mime: 'image/gif', base64: s.replace(/\s+/g, '') };
  if (s.startsWith('UklGR')) return { mime: 'image/webp', base64: s.replace(/\s+/g, '') };

  return null;
}

// ---------- SessionMultiSelect (NO MUI) ----------
function SessionMultiSelect({ options, value, onChange, placeholder = 'Choose sessions...' }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = Array.isArray(value) ? value : [];
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const base = Array.isArray(options) ? options : [];
    if (!q) return base;
    return base.filter((x) => String(x).toLowerCase().includes(q));
  }, [options, filter]);

  const add = (name) => {
    if (!name) return;
    if (selectedSet.has(name)) return;
    onChange([...selected, name]);
  };

  const remove = (name) => {
    onChange(selected.filter((x) => x !== name));
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen(true)}
        style={{
          minHeight: 42,
          border: '1px solid rgba(0,0,0,0.16)',
          borderRadius: 10,
          padding: 8,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'center',
          background: 'rgba(0,0,0,0.02)',
          cursor: 'text'
        }}
      >
        {selected.length === 0 ? (
          <span style={{ opacity: 0.6, fontSize: 14 }}>{placeholder}</span>
        ) : (
          selected.map((s) => (
            <span
              key={s}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '4px 10px',
                borderRadius: 999,
                background: 'rgba(0,0,0,0.06)',
                fontSize: 13
              }}
            >
              <span
                style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                }}
              >
                {s}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(s);
                }}
                style={{
                  border: 0,
                  background: 'transparent',
                  color: 'rgba(0,0,0,0.6)',
                  cursor: 'pointer',
                  fontSize: 14,
                  lineHeight: 1
                }}
                aria-label={`remove-${s}`}
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>

      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 20,
            left: 0,
            right: 0,
            marginTop: 8,
            border: '1px solid rgba(0,0,0,0.16)',
            borderRadius: 12,
            background: '#ffffff',
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: 10, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Type to filter..."
              style={{
                width: '100%',
                height: 36,
                borderRadius: 10,
                border: '1px solid rgba(0,0,0,0.16)',
                background: 'rgba(0,0,0,0.02)',
                padding: '0 12px',
                outline: 'none'
              }}
              autoFocus
            />
          </div>

          <div style={{ maxHeight: 260, overflow: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 12, opacity: 0.7, fontSize: 14 }}>No sessions</div>
            ) : (
              filtered.map((name) => {
                const isSel = selectedSet.has(name);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => add(name)}
                    disabled={isSel}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 12px',
                      border: 0,
                      background: isSel ? 'rgba(0,0,0,0.04)' : 'transparent',
                      cursor: isSel ? 'not-allowed' : 'pointer',
                      opacity: isSel ? 0.65 : 1
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                      }}
                    >
                      {name}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <div
            style={{
              padding: 10,
              borderTop: '1px solid rgba(0,0,0,0.08)',
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end'
            }}
          >
            <button
              type="button"
              onClick={() => {
                onChange([]);
                setFilter('');
              }}
              style={{
                border: '1px solid rgba(0,0,0,0.16)',
                background: 'transparent',
                borderRadius: 10,
                padding: '6px 10px',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                border: 0,
                background: 'rgba(0,0,0,0.06)',
                borderRadius: 10,
                padding: '6px 10px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- main component ----------
export default function Chat() {
  const AUTH = useMemo(
    () => ({
      withCredentials: true,
      headers: { 'X-API-Key': 'secret123' }
    }),
    []
  );

  // ====== Sessions endpoint config ======
  const SESSIONS_LIMIT = 100;
  const SESSIONS_PAGE = 1;

  // Chat list paging
  const CHAT_LIST_LIMIT = 20;

  const [sessionOptions, setSessionOptions] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);

  // ✅ active=true / false toggle
  const [sessionsActiveOnly, setSessionsActiveOnly] = useState(true);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const [chats, setChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(false);

  // unread badge per chat
  const [unreadByKey, setUnreadByKey] = useState({});
  const chatsRef = useRef([]);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  // page + hasMore per session
  const [chatPageBySession, setChatPageBySession] = useState({});
  const [hasMoreBySession, setHasMoreBySession] = useState({});

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const [activeChat, setActiveChat] = useState(null);

  const [searchChat, setSearchChat] = useState('');
  const [remoteSearching, setRemoteSearching] = useState(false);

  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);

  // media (img / video)
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState(null);
  const mediaInputRef = useRef(null);

  // WS control
  const [wsEnabled, setWsEnabled] = useState(false);

  // WS refs
  const wsRef = useRef(null);
  const wsRetryRef = useRef(0);
  const activeChatRef = useRef(null);
  const selectedSessionsRef = useRef([]);
  const wsEnabledRef = useRef(false);

  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);

  useEffect(() => {
    selectedSessionsRef.current = selectedSessions;
  }, [selectedSessions]);

  useEffect(() => {
    wsEnabledRef.current = wsEnabled;
  }, [wsEnabled]);

  useEffect(() => {
    let url;
    if (mediaFile) {
      url = URL.createObjectURL(mediaFile);
      setMediaPreviewUrl(url);
    } else {
      setMediaPreviewUrl(null);
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [mediaFile]);

  // reset when no sessions picked
  useEffect(() => {
    if (!selectedSessions.length) {
      setWsEnabled(false);
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
      wsRetryRef.current = 0;

      setChats([]);
      setActiveChat(null);
      setMessages([]);
      setUnreadByKey({});

      setChatPageBySession({});
      setHasMoreBySession({});
    }
  }, [selectedSessions.length]);

  // if selected sessions set changes, reset chat/message list for consistency
  useEffect(() => {
    if (!selectedSessions.length) return;

    setChats([]);
    setActiveChat(null);
    setMessages([]);
    setUnreadByKey({});

    setChatPageBySession({});
    setHasMoreBySession({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSessions.join('|')]);

  // ====== sessions fetch (NEW ENDPOINT) ======
  const fetchSessions = async (activeFlag = sessionsActiveOnly) => {
    setSessionsLoading(true);
    try {
      const url =
        `/api/sessions/all/${SESSIONS_LIMIT}/${SESSIONS_PAGE}` + `?active=${activeFlag ? 'true' : 'false'}` + `&sortBy=session_name`;

      const res = await api.get(url, AUTH);
      const payload = res?.data || {};

      const list =
        (Array.isArray(payload.sessions) && payload.sessions) ||
        (Array.isArray(payload.data) && payload.data) ||
        (Array.isArray(payload.items) && payload.items) ||
        [];

      const uniq = [];
      const seen = new Set();

      list.forEach((s) => {
        const name = s?.sessionName || s?.name || s?.id;
        if (!name || seen.has(name)) return;
        seen.add(name);
        uniq.push(String(name));
      });

      setSessionOptions(uniq);
    } catch (e) {
      console.error('fetch sessions error:', e);
      setSessionOptions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  // initial load
  useEffect(() => {
    fetchSessions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // when toggle active/inactive, reload options + reset selection & lists
  useEffect(() => {
    // reset everything related to selection
    setSelectedSessions([]);
    setChats([]);
    setActiveChat(null);
    setMessages([]);
    setUnreadByKey({});
    setChatPageBySession({});
    setHasMoreBySession({});

    setWsEnabled(false);
    try {
      wsRef.current?.close();
    } catch {}
    wsRef.current = null;
    wsRetryRef.current = 0;

    fetchSessions(sessionsActiveOnly);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionsActiveOnly]);

  // ====== WS connect ======
  const connectWs = () => {
    try {
      const host = window.location.hostname;
      const WS_URL = `ws://${host}:3000/ws/messages`;

      if (!wsEnabledRef.current) return;
      const picked = selectedSessionsRef.current || [];
      if (!picked.length) return;

      if (wsRef.current && (wsRef.current.readyState === 0 || wsRef.current.readyState === 1)) return;

      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        wsRetryRef.current = 0;
        console.log('[WS] connected', { url: WS_URL, readyState: ws.readyState });
      };

      ws.onmessage = (evt) => {
        try {
          if (!wsEnabledRef.current) return;
          const pickedNow = selectedSessionsRef.current || [];
          if (!pickedNow.length) return;

          const raw = JSON.parse(evt.data);

          if (raw?.type === 'connected') {
            console.log('[WS] handshake payload', raw);
            return;
          }

          // ✅ IMPORTANT: unread event is the source-of-truth for which chat has unread
          if (raw?.type === 'chat_unread') {
            const sessionName = raw?.sessionName || '';
            const chatId = raw?.chatId || '';
            const unread = Number(raw?.unread || 0);

            if (!sessionName || !chatId) return;
            if (!pickedNow.includes(sessionName)) return;

            const key = `${sessionName}|${chatId}`;

            setUnreadByKey((prev) => ({ ...prev, [key]: unread }));

            // bump chat to top so badge appears at correct row
            setChats((prev) => {
              const list = Array.isArray(prev) ? [...prev] : [];
              const idx = list.findIndex((c) => c.sessionName === sessionName && c.chatId === chatId);
              const item = { sessionName, chatId };

              if (idx === 0) return list;
              if (idx > 0) list.splice(idx, 1);
              list.unshift(item);

              if (list.length > 300) list.length = 300;
              return list;
            });

            return;
          }

          // normal message event
          const data = normalizeWsMessage(raw);

          if (!pickedNow.includes(data.sessionName)) return;

          // keep chat list updated (best-effort; unread positioning is handled by chat_unread)
          setChats((prev) => {
            const list = Array.isArray(prev) ? [...prev] : [];
            const key = `${data.sessionName}|${data.chatId}`;
            const idx = list.findIndex((c) => `${c.sessionName}|${c.chatId}` === key);
            const item = { sessionName: data.sessionName, chatId: data.chatId };

            if (idx === 0) return list;
            if (idx > 0) list.splice(idx, 1);
            list.unshift(item);

            if (list.length > 300) list.length = 300;
            return list;
          });

          const ac = activeChatRef.current;
          if (ac && ac.sessionName === data.sessionName && ac.chatId === data.chatId) {
            setMessages((prev) => {
              const list = Array.isArray(prev) ? prev : [];
              if (data.messageId && list.some((m) => m.messageId === data.messageId)) return list;
              if (!data.messageId && data.id && list.some((m) => m.id === data.id)) return list;
              return [...list, data];
            });
          }
        } catch (e) {
          console.error('[WS] parse error:', e);
        }
      };

      ws.onclose = (evt) => {
        if (!wsEnabledRef.current) return;

        const retry = wsRetryRef.current + 1;
        wsRetryRef.current = retry;

        const delay = Math.min(1000 * Math.pow(2, Math.min(retry, 5)), 15000);
        console.log('[WS] closed', {
          code: evt?.code,
          reason: evt?.reason,
          wasClean: evt?.wasClean,
          retryInMs: delay
        });

        setTimeout(() => {
          if (!wsEnabledRef.current) return;
          const pickedNow = selectedSessionsRef.current || [];
          if (!pickedNow.length) return;
          connectWs();
        }, delay);
      };

      ws.onerror = (err) => {
        console.error('[WS] error', { event: err, readyState: ws.readyState, url: WS_URL });
      };
    } catch (e) {
      console.error('[WS] connect error:', e);
    }
  };

  useEffect(() => {
    if (!wsEnabled) {
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
      wsRetryRef.current = 0;
      return;
    }

    if (!selectedSessions.length) return;

    connectWs();

    return () => {
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsEnabled, selectedSessions.length]);

  // --------- CHAT LIST (Pagination) ----------
  const mergeChatsUnique = (prev, incoming) => {
    const a = Array.isArray(prev) ? prev : [];
    const b = Array.isArray(incoming) ? incoming : [];

    const map = new Map();
    [...a, ...b].forEach((c) => {
      if (!c?.sessionName || !c?.chatId) return;
      const key = `${c.sessionName}|${c.chatId}`;
      if (!map.has(key)) map.set(key, c);
    });

    return Array.from(map.values());
  };

  const fetchChatsPages = async ({ pagesBySession, reset }) => {
    if (!selectedSessions.length) return;
    setChatsLoading(true);

    try {
      const all = [];
      const nextHasMore = {};
      const nextPages = {};

      for (const s of selectedSessions) {
        const page = pagesBySession?.[s] ?? 1;
        const ss = encodeURIComponent(s);

        const res = await api.get(`/api/messages/chats/${CHAT_LIST_LIMIT}/${page}?sessionName=${ss}`, AUTH);
        const payload = res?.data || {};
        const list = Array.isArray(payload.chats) ? payload.chats : [];

        list.forEach((c) => {
          if (c?.sessionName && c?.chatId) all.push({ sessionName: c.sessionName, chatId: c.chatId });
        });

        nextHasMore[s] = list.length === CHAT_LIST_LIMIT;
        nextPages[s] = page;
      }

      setChats((prev) => (reset ? mergeChatsUnique([], all) : mergeChatsUnique(prev, all)));
      setHasMoreBySession((prev) => ({ ...prev, ...nextHasMore }));
      setChatPageBySession((prev) => ({ ...prev, ...nextPages }));

      if (reset) {
        setActiveChat(null);
        setMessages([]);
        setUnreadByKey({});
      }
    } catch (e) {
      console.error('fetch chats error:', e);
      if (reset) setChats([]);
    } finally {
      setChatsLoading(false);
    }
  };

  const handleLoadChats = async () => {
    if (!selectedSessions.length) return;
    setWsEnabled(true);
    setUnreadByKey({});

    const pages = {};
    selectedSessions.forEach((s) => (pages[s] = 1));
    await fetchChatsPages({ pagesBySession: pages, reset: true });
  };

  const handleLoadMoreChats = async () => {
    if (!selectedSessions.length) return;

    const pages = {};
    let any = false;

    selectedSessions.forEach((s) => {
      const hasMore = hasMoreBySession[s];
      if (hasMore === false) return;

      const cur = chatPageBySession[s] || 1;
      pages[s] = cur + 1;
      any = true;
    });

    if (!any) return;

    await fetchChatsPages({ pagesBySession: pages, reset: false });
  };

  const anyHasMore = selectedSessions.some((s) => hasMoreBySession[s] !== false);

  // --------- fetch messages ----------
  const fetchMessagesForActive = async (target) => {
    const chat = target || activeChat;
    if (!chat) return;

    setMessagesLoading(true);
    try {
      const s = encodeURIComponent(chat.sessionName);
      const cid = encodeURIComponent(chat.chatId);

      const res = await api.get(`/api/messages/chats/${s}/${cid}`, AUTH);
      const payload = res?.data || {};
      const list = Array.isArray(payload.messages) ? payload.messages : [];

      const sorted = [...list].sort((a, b) => {
        const ta = new Date(a.createdAt).getTime();
        const tb = new Date(b.createdAt).getTime();
        return ta - tb;
      });

      setMessages(sorted);
    } catch (e) {
      console.error('fetch messages error:', e);
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleSelectChat = (c) => {
    setActiveChat(c);

    // reset unread for this chat
    const key = `${c.sessionName}|${c.chatId}`;
    setUnreadByKey((prev) => ({ ...prev, [key]: 0 }));

    fetchMessagesForActive(c);
  };

  // --------- remote search ----------
  const handleSearchChatsRemote = async () => {
    const q = searchChat.trim();
    if (!q) return;

    if (selectedSessions.length !== 1) return;
    if (!isLikelyNumberQuery(q) && !q.includes('@')) return;

    setRemoteSearching(true);
    try {
      const sessionName = encodeURIComponent(selectedSessions[0]);
      const waId = normalizeChatIdInputToWaId(q);
      const chatIdParam = encodeURIComponent(waId.replace('@c.us', ''));

      const res = await api.get(`/api/messages/chats?sessionName=${sessionName}&chatId=${chatIdParam}`, AUTH);
      const payload = res?.data || {};
      const list = Array.isArray(payload.chats) ? payload.chats : [];

      if (list.length) {
        setChats((prev) => {
          const merged = Array.isArray(prev) ? [...prev] : [];
          const seen = new Set(merged.map((x) => `${x.sessionName}|${x.chatId}`));

          for (const c of list) {
            if (!c?.sessionName || !c?.chatId) continue;
            const key = `${c.sessionName}|${c.chatId}`;
            if (seen.has(key)) continue;
            seen.add(key);
            merged.unshift({ sessionName: c.sessionName, chatId: c.chatId });
          }

          if (merged.length > 300) merged.length = 300;
          return merged;
        });
      }
    } catch (e) {
      console.error('remote search chats error:', e);
    } finally {
      setRemoteSearching(false);
    }
  };

  const filteredChats = useMemo(() => {
    const q = searchChat.trim().toLowerCase();
    if (!q) return chats;

    return chats.filter((c) => {
      const num = formatWaNumber(c.chatId).toLowerCase();
      return num.includes(q) || c.chatId.toLowerCase().includes(q) || c.sessionName.toLowerCase().includes(q);
    });
  }, [chats, searchChat]);

  const getCurrentRecipient = () => {
    if (!activeChat) return '';
    if (!messages || !messages.length) return activeChat.chatId;

    const reversed = [...messages].reverse();

    const lastIncoming = reversed.find((m) => m.direction === 'incoming' && m.fromWaId);
    if (lastIncoming) return lastIncoming.fromWaId;

    const lastOutgoing = reversed.find((m) => m.direction === 'outgoing' && m.toNumber);
    if (lastOutgoing) return lastOutgoing.toNumber;

    return activeChat.chatId;
  };

  const handleClickAttach = () => {
    if (!activeChat || sending) return;
    mediaInputRef.current?.click();
  };

  const handleMediaChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setMediaFile(null);
      return;
    }
    setMediaFile(file);
  };

  const clearMedia = () => {
    setMediaFile(null);
    if (mediaInputRef.current) mediaInputRef.current.value = '';
  };

  function toRecipientNumber(id) {
    if (!id) return '';
    const s = String(id).trim();
    if (s.includes('@')) return s.split('@')[0];
    return s.replace(/[^\d]/g, '');
  }

  // SEND RULES:
  // - kalau TEXT ada => kirim template promo_button
  // - kalau MEDIA doang (text kosong) => JANGAN kirim template sama sekali
  const handleSend = async () => {
    if (!activeChat) return;

    const text = inputValue.trim();
    const hasText = !!text;
    const hasMedia = !!mediaFile;

    if (!hasText && !hasMedia) return;

    const recipientWaid = getCurrentRecipient();
    const recipientNumber = toRecipientNumber(recipientWaid);

    setSending(true);
    let fallbackTimer;

    try {
      const formData = new FormData();
      formData.append('sessionNames', activeChat.sessionName);
      formData.append('intervalSeconds', '1');
      formData.append('maxMessagesPerSession', '1');
      formData.append('recipients', recipientNumber);

      if (hasText) {
        const tpl = buildReplyTemplate(text);
        formData.append('template', JSON.stringify(tpl));
      }

      if (hasMedia) {
        formData.append('image', mediaFile);
      }

      fallbackTimer = setTimeout(() => {
        fetchMessagesForActive();
      }, 1200);

      await api.post('/api/messages/chat', formData, AUTH);

      setInputValue('');
      clearMedia();
    } catch (e) {
      console.error('send message via chat error:', e);
      if (fallbackTimer) clearTimeout(fallbackTimer);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDownInput = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleKeyDownSearch = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await handleSearchChatsRemote();
    }
  };

  // render: parse template JSON, tampilkan body + links (button url)
  const renderMessageBody = (m) => {
    const raw = m?.message || '';

    const rawBase64Media = !m?.mediaBase64 && !m?.mediaUrl ? detectBase64MediaInMessage(raw) : null;

    const tplInfo = extractTemplateInfo(raw);
    const extractedBody = tplInfo.bodyText;
    const extractedLinks = tplInfo.urlButtons;

    const caption = (rawBase64Media ? '' : extractedBody || (isTemplatePayloadString(raw) ? '' : raw)).trim();
    const showCaption = !!caption && !isPlaceholder(caption);

    const links = (Array.isArray(extractedLinks) ? extractedLinks : []).filter(Boolean);
    const showLinks = links.length > 0;

    const mediaStyleCommon = {
      display: 'block',
      width: 'auto',
      maxWidth: 'min(520px, 100%)',
      height: 'auto',
      borderRadius: 10
    };

    const Media = () => {
      if (m?.mediaBase64 && m?.mediaMimeType) {
        const url = buildMediaDataUrl(m.mediaMimeType, m.mediaBase64);
        if (m.mediaMimeType.startsWith('image/')) {
          return <img src={url} alt="image" style={{ ...mediaStyleCommon, objectFit: 'cover' }} />;
        }
        if (m.mediaMimeType.startsWith('video/')) {
          return <video src={url} controls preload="metadata" style={mediaStyleCommon} />;
        }
      }

      if (m?.mediaUrl) {
        const url = m.mediaUrl;
        const lower = String(url).toLowerCase();
        const isVideo = lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.mov');
        if (isVideo) return <video src={url} controls preload="metadata" style={mediaStyleCommon} />;
        return <img src={url} alt="image" style={{ ...mediaStyleCommon, objectFit: 'cover' }} />;
      }

      if (rawBase64Media?.mime && rawBase64Media?.base64) {
        const url = buildMediaDataUrl(rawBase64Media.mime, rawBase64Media.base64);
        return <img src={url} alt="image" style={{ ...mediaStyleCommon, objectFit: 'cover' }} />;
      }

      return null;
    };

    const mediaNode = Media();
    const hasMedia = !!mediaNode;

    if (hasMedia) {
      return (
        <Box sx={{ mt: 0.25 }}>
          {mediaNode}

          {(showCaption || showLinks) && (
            <Box sx={{ mt: 0.75 }}>
              {showCaption && (
                <Typography sx={{ fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#e9edef' }}>{caption}</Typography>
              )}

              {showLinks && (
                <Box sx={{ mt: showCaption ? 0.75 : 0 }}>
                  <Typography sx={{ fontSize: 13, color: '#e9edef', opacity: 0.9 }}>Links:</Typography>
                  {links.map((u, i) => (
                    <Typography key={`${u}-${i}`} sx={{ fontSize: 13, color: '#bfe9ff', wordBreak: 'break-word' }}>
                      -{' '}
                      <a href={u} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                        {u}
                      </a>
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      );
    }

    if (!caption && !showLinks) return null;

    return (
      <Box>
        {showCaption && (
          <Typography sx={{ fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#e9edef' }}>{caption}</Typography>
        )}

        {showLinks && (
          <Box sx={{ mt: showCaption ? 0.75 : 0 }}>
            <Typography sx={{ fontSize: 13, color: '#e9edef', opacity: 0.9 }}>Links:</Typography>
            {links.map((u, i) => (
              <Typography key={`${u}-${i}`} sx={{ fontSize: 13, color: '#bfe9ff', wordBreak: 'break-word' }}>
                -{' '}
                <a href={u} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                  {u}
                </a>
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: 'calc(100vh - 64px)',
        maxHeight: '100vh',
        display: 'flex',
        bgcolor: '#0a1014',
        color: '#f5f5f5',
        fontFamily: '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
      }}
    >
      {/* LEFT: chats panel */}
      <Box
        sx={{
          width: { xs: '35%', md: 340 },
          borderRight: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* top bar - sessions */}
        <Box sx={{ px: 2, py: 1.5, bgcolor: '#202c33', borderBottom: '1px solid rgba(0,0,0,0.6)' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
            <Typography variant="subtitle2">Sessions</Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant={sessionsActiveOnly ? 'contained' : 'outlined'}
                onClick={() => setSessionsActiveOnly(true)}
                disabled={sessionsLoading}
                sx={{
                  minWidth: 72,
                  ...(sessionsActiveOnly
                    ? { bgcolor: '#00a884', '&:hover': { bgcolor: '#00a884cc' } }
                    : { color: '#e9edef', borderColor: 'rgba(255,255,255,0.22)' })
                }}
              >
                Active
              </Button>

              <Button
                size="small"
                variant={!sessionsActiveOnly ? 'contained' : 'outlined'}
                onClick={() => setSessionsActiveOnly(false)}
                disabled={sessionsLoading}
                sx={{
                  minWidth: 78,
                  ...(!sessionsActiveOnly
                    ? { bgcolor: '#00a884', '&:hover': { bgcolor: '#00a884cc' } }
                    : { color: '#e9edef', borderColor: 'rgba(255,255,255,0.22)' })
                }}
              >
                Inactive
              </Button>

              <Tooltip title="Reload sessions list">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => fetchSessions(sessionsActiveOnly)}
                    disabled={sessionsLoading}
                    sx={{ color: '#e9edef' }}
                  >
                    <ReloadOutlined style={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <SessionMultiSelect
            options={sessionOptions}
            value={selectedSessions}
            onChange={setSelectedSessions}
            placeholder={
              sessionsLoading ? 'Loading sessions...' : sessionsActiveOnly ? 'Pilih sessions aktif...' : 'Pilih sessions non-aktif...'
            }
          />

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <Button size="small" variant="contained" onClick={handleLoadChats} disabled={!selectedSessions.length || chatsLoading}>
              {chatsLoading ? 'Loading…' : 'Load chats'}
            </Button>

            <Box sx={{ flex: 1 }} />

            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              {sessionsActiveOnly ? 'Filter: active=true' : 'Filter: active=false'} • Selected: {selectedSessions.length}
            </Typography>
          </Stack>
        </Box>

        {/* search chats */}
        <Box sx={{ px: 1.5, py: 1, bgcolor: '#111b21', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <TextField
            size="small"
            fullWidth
            variant="outlined"
            placeholder={selectedSessions.length === 1 ? 'Search number… (Enter = remote search)' : 'Search number / chatId…'}
            value={searchChat}
            onChange={(e) => setSearchChat(e.target.value)}
            onKeyDown={handleKeyDownSearch}
            InputProps={{
              sx: {
                fontSize: 13,
                bgcolor: '#202c33',
                borderRadius: '999px',
                color: '#e9edef',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.16)' },
                '& .MuiInputBase-input': { color: '#e9edef' },
                '& input::placeholder': { color: 'rgba(233,237,239,0.65)', opacity: 1 }
              },
              endAdornment: remoteSearching ? (
                <Box sx={{ pr: 1, display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={16} sx={{ color: '#e9edef' }} />
                </Box>
              ) : null
            }}
          />
          {selectedSessions.length !== 1 && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.65 }}>
              Remote search butuh 1 session terpilih.
            </Typography>
          )}
        </Box>

        {/* chats list */}
        <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: '#111b21' }}>
          {chatsLoading && (
            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!chatsLoading && !chats.length && (
            <Typography variant="body2" sx={{ p: 2, opacity: 0.7 }}>
              Pilih session dan klik &quot;Load chats&quot; untuk melihat nomor yang ngechat.
            </Typography>
          )}

          {!chatsLoading &&
            filteredChats.map((c) => {
              const isActive = activeChat && activeChat.chatId === c.chatId && activeChat.sessionName === c.sessionName;
              const label = formatWaNumber(c.chatId);

              const key = `${c.sessionName}|${c.chatId}`;
              const unread = unreadByKey[key] || 0;

              return (
                <Box
                  key={`${c.sessionName}|${c.chatId}`}
                  onClick={() => handleSelectChat(c)}
                  sx={{
                    px: 2,
                    py: 1.25,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: isActive ? '#202c33' : 'transparent',
                    '&:hover': { bgcolor: '#202c33' },
                    borderBottom: '1px solid rgba(255,255,255,0.04)'
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#00a884',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      fontSize: 18,
                      fontWeight: 600
                    }}
                  >
                    {label.replace('+', '').charAt(0)}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap sx={{ fontSize: 14, color: '#e9edef', mb: 0.25 }}>
                      {label}
                    </Typography>
                    <Typography variant="caption" noWrap sx={{ opacity: 0.7, fontSize: 11 }}>
                      {c.chatId} <span style={{ opacity: 0.7 }}>• {c.sessionName}</span>
                    </Typography>
                  </Box>

                  {unread > 0 && (
                    <Box
                      sx={{
                        ml: 1,
                        minWidth: 22,
                        height: 22,
                        px: 0.75,
                        borderRadius: '999px',
                        bgcolor: '#00a884',
                        color: '#0b141a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700
                      }}
                    >
                      {unread}
                    </Box>
                  )}
                </Box>
              );
            })}
        </Box>

        {/* footer pagination */}
        <Box sx={{ p: 1.25, borderTop: '1px solid rgba(255,255,255,0.08)', bgcolor: '#111b21' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              onClick={handleLoadMoreChats}
              disabled={!selectedSessions.length || chatsLoading || !anyHasMore}
              sx={{ color: '#e9edef', borderColor: 'rgba(255,255,255,0.22)' }}
            >
              {chatsLoading ? 'Loading…' : 'Load more'}
            </Button>

            <Box sx={{ flex: 1 }} />

            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              {selectedSessions.length
                ? `Loaded: ${chats.length} • ${selectedSessions.filter((s) => hasMoreBySession[s] !== false).length}/${selectedSessions.length} sessions has more`
                : '—'}
            </Typography>
          </Stack>
        </Box>
      </Box>

      {/* RIGHT: chat area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#0b141a' }}>
        {/* header */}
        <Box
          sx={{
            height: 60,
            px: 2,
            bgcolor: '#202c33',
            borderBottom: '1px solid rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          {activeChat ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: '#008069',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    fontWeight: 600
                  }}
                >
                  {formatWaNumber(activeChat.chatId).replace('+', '').charAt(0)}
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#e9edef' }}>
                    {formatWaNumber(activeChat.chatId)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    chatId: {activeChat.chatId} • session: {activeChat.sessionName}
                  </Typography>
                </Box>
              </Box>

              <Stack direction="row" spacing={1}>
                <Tooltip title="Refresh messages">
                  <IconButton size="small" sx={{ color: '#e9edef' }} onClick={() => fetchMessagesForActive()}>
                    <ReloadOutlined style={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </>
          ) : (
            <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
              Pilih chat di panel kiri untuk melihat isi pesan.
            </Typography>
          )}
        </Box>

        {/* messages list */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 3,
            py: 2,
            backgroundImage: 'radial-gradient(circle at 0 0, #18252f, #0b141a)'
          }}
        >
          {messagesLoading && (
            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={26} />
            </Box>
          )}

          {!messagesLoading && activeChat && !messages.length && (
            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', opacity: 0.7 }}>
              Belum ada pesan untuk chat ini.
            </Typography>
          )}

          {!activeChat && (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="h6" sx={{ color: '#8696a0', textAlign: 'center', maxWidth: 320 }}>
                Selamat datang di panel Chat.
                <br />
                Pilih session & nomor di sisi kiri untuk mulai membaca dan membalas pesan.
              </Typography>
            </Box>
          )}

          {activeChat &&
            messages.map((m) => {
              const isOutgoing = m.direction === 'outgoing';
              const align = isOutgoing ? 'flex-end' : 'flex-start';
              const bg = isOutgoing ? '#005c4b' : '#202c33';
              const radius = isOutgoing ? '16px 0 16px 16px' : '0 16px 16px 16px';

              return (
                <Box key={m.messageId || m.id} sx={{ display: 'flex', justifyContent: align, mb: 0.75 }}>
                  <Box
                    sx={{
                      maxWidth: '75%',
                      bgcolor: bg,
                      borderRadius: radius,
                      px: 1.25,
                      py: 0.75,
                      boxShadow: '0 1px 0 rgba(0,0,0,0.3)',
                      position: 'relative'
                    }}
                  >
                    {renderMessageBody(m)}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.4, gap: 0.5 }}>
                      <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.7 }}>
                        {formatTime(m.createdAt)}
                      </Typography>
                      {isOutgoing && (
                        <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.7 }}>
                          {m.status}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
        </Box>

        {/* attachment preview */}
        {mediaFile && mediaPreviewUrl && (
          <Box sx={{ borderTop: '1px solid #202c33', bgcolor: '#202c33', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 96,
                height: 64,
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: '#111b21',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {mediaFile.type.startsWith('image/') ? (
                <img src={mediaPreviewUrl} alt="preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
              ) : (
                <video src={mediaPreviewUrl} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} muted />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontSize: 12, color: '#e9edef' }} noWrap title={mediaFile.name}>
                {mediaFile.name}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: 11, opacity: 0.7 }}>
                {(mediaFile.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
            <button
              type="button"
              onClick={clearMedia}
              style={{ border: 0, background: 'transparent', color: '#ff7777', cursor: 'pointer', fontSize: 12 }}
            >
              remove
            </button>
          </Box>
        )}

        {/* input bar */}
        <Box sx={{ borderTop: '1px solid #202c33', bgcolor: '#202c33', px: 2, py: 1.25 }}>
          <input ref={mediaInputRef} type="file" style={{ display: 'none' }} accept="image/*,video/*" onChange={handleMediaChange} />

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title="Attach image / video">
              <span>
                <IconButton onClick={handleClickAttach} disabled={!activeChat || sending} sx={{ color: mediaFile ? '#00a884' : '#e9edef' }}>
                  <PaperClipOutlined />
                </IconButton>
              </span>
            </Tooltip>

            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder={activeChat ? `Ketik pesan ke ${formatWaNumber(activeChat.chatId)}…` : 'Pilih chat dulu'}
              disabled={!activeChat || sending}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDownInput}
              InputProps={{
                sx: {
                  bgcolor: '#2a3942',
                  borderRadius: '24px',
                  fontSize: 14,
                  color: '#e9edef',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.16)' },
                  '& .MuiInputBase-input': { color: '#e9edef' },
                  '& input::placeholder': { color: 'rgba(233,237,239,0.65)', opacity: 1 }
                }
              }}
            />

            <IconButton
              disabled={!activeChat || (!inputValue.trim() && !mediaFile) || sending}
              onClick={handleSend}
              sx={{ bgcolor: '#00a884', '&:hover': { bgcolor: '#00a884cc' } }}
            >
              {sending ? (
                <CircularProgress size={20} sx={{ color: '#ffffff' }} />
              ) : (
                <SendOutlined style={{ color: '#ffffff', fontSize: 18 }} />
              )}
            </IconButton>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
